import { PendingToggle } from "@/pages/config/maintenance/schema/maintenance.schema";
import {
  getModuleLabel,
  getSectionLabel,
  SectionMap,
} from "@/pages/config/maintenance/schema/section-labels";
import { Box, Flex, Text } from "@chakra-ui/react";
import { MaintenanceSwitchRow } from "./MaintenanceSwitchRow";
import { useWatch } from "react-hook-form";
import { useMemo } from "react";
import { MaintenanceSections } from "@/constants/enums";
import { getSectionDisabledState, sortSections } from "./module-sort";

/**
 * Tipo de objeto que representa las props de un componente de tarjeta de módulo.
 */
interface MaintenanceModuleCardProps {
  moduleCode: string;
  sections: SectionMap;
  control: any;
  disabled: boolean;
  moduleGateOn: boolean;
  loadingFieldName: string | null;
  onRequestConfirm: (args: PendingToggle) => void;
}

/**
 * Componente de tarjeta de módulo.
 * @param props Props del componente.
 * @returns JSX del componente.
 */

export const MaintenanceModuleCard = ({
  moduleCode,
  sections,
  control,
  disabled,
  moduleGateOn,
  loadingFieldName,
  onRequestConfirm,
}: MaintenanceModuleCardProps) => {
  // Observar el valor actual del switch de SALES para granaria
  const salesSwitchValue = useWatch({
    control,
    name: `${moduleCode}:${MaintenanceSections.Sales}`,
  });

  // Memoizar las entradas ordenadas para evitar re-ordenamiento innecesario
  const sectionEntries = useMemo(
    () => Object.entries(sections ?? {}).sort(sortSections(moduleCode)),
    [sections, moduleCode],
  );

  return (
    <Box
      key={moduleCode}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="8px"
      overflow="hidden"
    >
      <Flex
        padding="10px 12px"
        bgColor="gray.50"
        alignItems="center"
        justifyContent="space-between"
      >
        <Text
          fontWeight={700}
          fontSize="12px"
          textTransform="uppercase"
          color="gray.600"
        >
          {getModuleLabel(moduleCode)}
        </Text>
        <Text
          fontWeight={700}
          fontSize="10px"
          textTransform="uppercase"
          color="gray.500"
        >
          Activar mantenimiento
        </Text>
      </Flex>

      <Flex flexDir="column">
        {sectionEntries.map(([sectionCode], idx) => {
          const fieldName = `${moduleCode}:${sectionCode}`;
          const rowDisabled = getSectionDisabledState(
            moduleCode,
            sectionCode,
            disabled,
            moduleGateOn,
            salesSwitchValue,
          );

          return (
            <Flex
              key={fieldName}
              width="100%"
              padding="10px 12px"
              borderTop={idx === 0 ? "none" : "1px solid"}
              borderTopColor={idx === 0 ? undefined : "gray.100"}
            >
              <MaintenanceSwitchRow
                control={control}
                fieldName={fieldName}
                label={getSectionLabel(sectionCode)}
                disabled={rowDisabled}
                showSpinner={loadingFieldName === fieldName}
                onRequestConfirm={onRequestConfirm}
                codigoModulo={moduleCode}
                codigoSeccion={sectionCode}
              />
            </Flex>
          );
        })}
      </Flex>
    </Box>
  );
};
